import React from 'react';
import { useStore } from '../context/StoreContext';
import { CoveristicLogo } from './CoveristicLogo';
import { 
  Clock, 
  LogIn
} from 'lucide-react';
import { getLocalizedPath, getCategoryPermalink } from '../utils/localization';

export const Footer: React.FC = () => {
  const { setCurrentView, setActiveCategoryFilter, setSelectedProduct, language, setLanguage, t } = useStore();

  const handleNav = (view: string, cat: string = 'all') => {
    setSelectedProduct(null);
    setCurrentView(view);
    setActiveCategoryFilter(cat);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, view: string, cat: string = 'all') => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    handleNav(view, cat);
  };

  return (
    <footer className="bg-[#0c0c0c] text-white border-t border-neutral-800 pt-12 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Top Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-12 border-b border-neutral-800">
          
          {/* Col 1 & 2: Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={getLocalizedPath('/', language)}
              onClick={(e) => handleLinkClick(e, 'home', 'all')}
              className="inline-block"
              aria-label="Coveristic Custom Print on Demand Studio Lebanon Footer Home"
            >
              <CoveristicLogo size="md" variant="box-red" />
            </a>
            
            <p className="text-xs text-neutral-400 max-w-sm leading-relaxed">
              {language === 'ar'
                ? 'استوديو Coveristic هو وجهتكم في لبنان للطباعة المخصصة والتصاميم حسب الطلب. نوفر طباعة فائقة الجودة على أغطية الهواتف لكافة الموديلات، ولوحات الكانفاس الجدارية، والأكواب الخزفية، والقمصان والسترات الشتوية، والملصقات المفرغة. يمكنكم إرسال صوركم أو تصاميمكم أو شعاركم الخاص — مع خدمة إرسال بروفة ومعاينة مجانية قبل الطباعة عبر واتساب.'
                : "Coveristic is Lebanon's dedicated custom print-on-demand studio. We specialize in high-quality personalized prints on Phone Cases for all brands, Wall Art Canvas, Ceramic Drinking Mugs, T-Shirts, Hoodies, and Die-Cut Stickers. Customers can upload their own images, photos, or artwork — image preview before printing request is available before printing on WhatsApp."
              }
            </p>

            {/* Helpful External Links & Language Switch */}
            <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
              <a
                href="https://www.instagram.com/coveristic"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-300 hover:text-[#E21B24] underline underline-offset-4 transition-colors"
              >
                {language === 'ar' ? 'تابعنا على إنستغرام (@coveristic)' : 'Follow @coveristic on Instagram'}
              </a>
              <span className="text-neutral-700">•</span>
              <a
                href="https://www.google.com/maps/place/Beirut,+Lebanon"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-300 hover:text-[#E21B24] underline underline-offset-4 transition-colors"
              >
                {language === 'ar' ? 'خريطة التوصيل في بيروت ولبنان' : 'Beirut & Lebanon Coverage Map'}
              </a>
              <span className="text-neutral-700">•</span>
              <a
                href={language === 'ar' ? '/' : '/ar/'}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  setLanguage(language === 'ar' ? 'en' : 'ar');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-[#E21B24] font-bold hover:underline transition-colors"
              >
                {language === 'ar' ? 'English Storefront (/)' : 'النسخة العربية (/ar/)'}
              </a>
            </div>
          </div>

          {/* Col 3: Shop Categories (Distinct descriptive anchor texts for SEO) */}
          <div className="space-y-3">
            <h4 className="text-xs font-black uppercase tracking-wider text-white font-heading">
              {t.customProducts}
            </h4>
            <ul className="space-y-2 text-xs text-neutral-400">
              <li>
                <a
                  href={getCategoryPermalink('phone-cases', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'phone-cases')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'كفرات هواتف مطبوعة حسب الطلب' : 'Personalized Phone Cases Catalog'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('canvas', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'canvas')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'لوحات كانفاس جدارية مشدودة' : 'Stretched Canvas Wall Art Prints'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('photo-frames', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'photo-frames')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'إطارات صور خشبية وجدارية' : 'Framed Wall & Desk Photo Prints'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('wood-crafts', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'wood-crafts')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'تحف وهدايا خشبية محفورة بالليزر' : 'Laser-Engraved Wood Craft Gifts'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('keychains', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'keychains')}
                className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'ميداليات مفاتيح بصوركم الخاصة' : 'Double-Sided Photo Keychains'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('mugs', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'mugs')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'أكواب قهوة وسيراميك مطبوعة' : 'Printed Ceramic Coffee Mugs'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('tshirts', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'tshirts')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'تيشيرتات قطنية مطبوعة DTF' : 'Heavyweight DTF Graphic T-Shirts'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('hoodies', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'hoodies')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'هوديز شتوية مبطنة بالصوف' : 'Fleece-Lined Streetwear Hoodies'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('stickers', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'stickers')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'ملصقات فينيل مقاومة للماء' : 'Waterproof Die-Cut Vinyl Stickers'}
                </a>
              </li>
              <li>
                <a
                  href={getCategoryPermalink('hats', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'hats')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'قبعات وكابات قطنية مخصصة' : 'Printed Cotton Caps & Snapbacks'}
                </a>
              </li>
            </ul>
          </div>

          {/* Col 4: Customer Services (Distinct anchor texts from Header) */}
          <div className="space-y-3">
            <h4 className="text-xs font-black uppercase tracking-wider text-white font-heading">
              {language === 'ar' ? 'الاستوديو والعروض' : 'Studio & Deals'}
            </h4>
            <ul className="space-y-2 text-xs text-neutral-400">
              <li>
                <a
                  href={getLocalizedPath('/shop', language)}
                  onClick={(e) => handleLinkClick(e, 'shop', 'all')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'تصفح جميع المنتجات في المتجر' : 'Shop Full Custom Catalog'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/offers', language)}
                  onClick={(e) => handleLinkClick(e, 'offers')}
                  className="text-[#E21B24] font-bold hover:underline transition-colors inline-flex items-center gap-1.5"
                >
                  <span>{language === 'ar' ? '🔥 عروض وتخفيضات الباقات' : '🔥 Bundle Discounts & Deals'}</span>
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/custom-print', language)}
                  onClick={(e) => handleLinkClick(e, 'custom-print')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'ارفع صورتك للطباعة حسب الطلب' : 'Upload Your Photo for Custom Print'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/about', language)}
                  onClick={(e) => handleLinkClick(e, 'about')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'عن استوديو كوفريستيك والأسئلة الشائعة' : 'About Coveristic Studio & FAQ'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/blog', language)}
                  onClick={(e) => handleLinkClick(e, 'blog')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'مقالات ودليل الطباعة في مدن لبنان' : 'Lebanon City Print Guides & Blog'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/contact', language)}
                  onClick={(e) => handleLinkClick(e, 'contact')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'نموذج الاتصال وخدمة الزبائن' : 'Customer Support & Contact Form'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/pricing', language)}
                  onClick={(e) => handleLinkClick(e, 'pricing')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'قائمة الأسعار الشاملة (USD)' : 'Full USD Price List & Delivery Rates'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/terms', language)}
                  onClick={(e) => handleLinkClick(e, 'terms')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'شروط الخدمة والضمان' : 'Terms of Service & Print Warranty'}
                </a>
              </li>
              <li>
                <a
                  href={getLocalizedPath('/privacy', language)}
                  onClick={(e) => handleLinkClick(e, 'privacy')}
                  className="hover:text-[#E21B24] transition-colors inline-block"
                >
                  {language === 'ar' ? 'سياسة الخصوصية وحماية الصور' : 'Privacy Policy & Photo Data Protection'}
                </a>
              </li>
            </ul>
          </div>

          {/* Col 5: Studio */}
          <div className="space-y-3">
            <div className="pt-2 text-[11px] text-neutral-400 flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-[#E21B24]" />
              <span>{language === 'ar' ? 'أوقات العمل: من الإثنين إلى السبت، 9 صباحاً - 7 مساءً' : 'Workshop Hours: Mon-Sat 9AM - 7PM'}</span>
            </div>
            <div className="pt-1 text-[11px] text-neutral-400">
              <span>{t.payOnDelivery}</span>
            </div>
          </div>

        </div>

        {/* Bottom copyright, login icon & hotline */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-neutral-500">
          <div className="flex flex-wrap items-center gap-2.5">
            <span>
              © {new Date().getFullYear()} <span className="text-neutral-300 font-bold">Coveristic</span>. {language === 'ar' ? 'جميع الحقوق محفوظة. استوديو الطباعة حسب الطلب في لبنان.' : 'All Rights Reserved. Custom Print on Demand Studio.'}
            </span>
            {/* Discreet Admin Login Icon */}
            <button
              id="footer-admin-login-icon-btn"
              onClick={() => handleNav('admin')}
              className="text-neutral-700 hover:text-neutral-300 transition-colors p-1 rounded hover:bg-neutral-800/80"
              title="Staff Login"
              aria-label="Staff Login"
            >
              <LogIn className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-4 text-[10px]">
            <a href="tel:+96181773349" className="hover:text-neutral-300 transition-colors">
              {language === 'ar' ? 'خط الطلبات المباشر: +961 81 773 349' : 'Direct Studio Hotline: +961 81 773 349'}
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};
